/**
 * features/marks/MarkEntry.tsx
 * ------------------------------------------------------------------
 * FEATURE #8 — term-aware mark entry, form-grouped for everyone.
 *
 *  Term 1 : monthly CA columns only. Term mark = average of entered CA
 *           months (ABS ignored). Grade from term mark.
 *  Term 2/3: monthly CA columns + a separate EXAM column. Grade &
 *           pass/fail come from the EXAM mark.
 *
 * ONE VIEW FOR ALL ROLES
 *  The user picks a SUBJECT and a FORM and sees every student in scope
 *  on ONE page, grouped under class sub-headers. Scope depends on who
 *  they are:
 *    • Linked teacher (subject/class teacher, and HODs who teach):
 *      their taught subjects; per-row editability via canEnterMarksFor.
 *      A class teacher additionally SEES their whole class for a subject
 *      (read-only for groups another teacher owns) — those read-only
 *      students are separated out under each class.
 *    • Manager / admin (no linked teacher): every offered subject, every
 *      class in the form. admin/headteacher edit all; other non-linked
 *      roles see only what canViewMarksFor permits (unchanged).
 *
 * Load and save are unified around the list of active classes (one
 * getDoc / one reportMarks doc per class). `studentId` is globally
 * unique, so a single draft / loadedEntries keyed by studentId safely
 * spans classes. Saving writes one doc per class with editable rows.
 *
 * Export: CSV and PDF, per class (icons on each class header) or for the
 * whole form (header buttons). CSV import (with a Class column to route
 * rows) feeds the same protected save() — month-locks and per-row
 * editability are never bypassed. There is no per-class Save button:
 * the single Save (and 30s auto-save) persists every class at once.
 * ------------------------------------------------------------------
 */
import {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  doc,
  getDoc,
  arrayUnion,
} from 'firebase/firestore';
/* No arrayRemove / deleteField here any more. Removing a pupil from a subject
   must also delete their mark entry, and firestore.rules forbids any client
   from removing a key from a marks document — so removal goes through the
   removeStudentFromSubject callable instead. See askToRemove below. */
import {
  Save,
  Lock,
  Info,
  Check,
  ChevronDown,
  ChevronUp,
  Upload,
  FileSpreadsheet,
  FileText,
  Users,
  Sparkles,
  Search,
  UserPlus,
  UserMinus,
  AlertTriangle,
  MessageSquare,
} from 'lucide-react';
import { db, setDoc, updateDoc, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  getMarkCategory,
  suggestComment,
  BUILT_IN_FALLBACKS,
  resolveTemplates,
  type SubjectTemplatesGeneral,
} from '@/lib/commentTemplates';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { isClientStale } from '@/lib/version';
import { useDirtyFlag } from '@/contexts/DirtyContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTermState } from '@/lib/useTermState';
import {
  canEnterMarksFor,
  canViewMarksFor,
  getAccessibleClasses,
  getLinkedTeacher,
} from '@/lib/permissions';
import { teacherTeachesSubjectInClass } from '@/lib/teacherLink';
import { getCaLabels } from '@/lib/assessmentConfig';
import {
  termUsesExam,
  deriveMark,
  normaliseMark,
  isBlank,
  shouldShowGrades,
  gradeColor,
  schemeForSubject,
} from '@/lib/grading';
import { sanitizeComment } from '@/lib/sanitize';
import StudentAvatar from '@/components/ui/StudentAvatar';
import { isJuneCohort } from '@/lib/cohort';
import { auditFromContext } from '@/lib/audit';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import {
  buildMarksGridPdf,
  type MarksPdfClass,
  type MarksPdfRow,
} from '@/lib/marksGridPdf';
import { Card, PageHeader, Spinner, Button } from '@/components/ui/primitives';
import { SaveConfirmation, useAutoSave } from '@/components/ui/SaveConfirmation';
import { TermKey, termLabel, SCHOOL } from '@/lib/school.config';
import type {
  MaybeMark,
  SubjectMarkEntry,
  Student,
  SchoolClass,
} from '@/types';

interface Props {
  year: string;
  term: TermKey;
}

type Draft = Record<
  string,
  { ca: Record<string, string>; exam: string; comment: string }
>;

interface RosterGroup {
  cls: SchoolClass;
  editable: Student[];
  readonly: Student[];
}

const rgb = (c: [number, number, number]) => `rgb(${c[0]},${c[1]},${c[2]})`;

/** Friendly label for a form level ('1'..'5' | 'AS' | 'A2'). */
function formLabel(form: string): string {
  if (!form) return '';
  if (form === 'AS' || form === 'A2') return form;
  return `Form ${form}`;
}

const slug = (s: string) => s.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
const disp = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '' : String(v);

export default function MarkEntry({ year, term }: Props) {
  const { activeStudents: students, subjects, classes, teachers, assessmentConfigs, loading, serverBuild } = useData();
  const { userData } = useAuth();
  const { state: termState, editable, isMonthLocked } =
    useTermState(year, term);

  const [subjectId, setSubjectId] = useState('');
  const [formId, setFormId] = useState('');
  // Deep-link: a teacher clicked a "your comment was flagged" notification.
  const [highlightStudentId, setHighlightStudentId] = useState<string | null>(null);
  const flagFixDone = useRef(false);
  // Approved comment bank (Firestore): the general doc + the current form's
  // sharded by-form doc. Feed the "Generate" button so it suggests real,
  // subject/form-specific phrasings mined from past comments, falling back to
  // the built-in bank when a bucket is empty.
  const [generalTpls, setGeneralTpls] = useState<SubjectTemplatesGeneral>({});
  const [formTpls, setFormTpls] = useState<SubjectTemplatesGeneral>({});
  const [draft, setDraft] = useState<Draft>({});
  const [openComment, setOpenComment] = useState<string | null>(null);
  // Comment fields open inline by default (faster entry — no click to expand);
  // teachers can collapse them for a compact view.
  const [commentsOpen, setCommentsOpen] = useState(true);
  // §1.3 — when a subject spans several classes (a set), sub-tabs let the
  // teacher focus one class at a time; 'all' shows every class on one page.
  const [classFilter, setClassFilter] = useState<string>('all');
  // #1 — the Mark Entry screen has two surfaces: entering marks, and managing
  // which students are on the teacher's subject list (add/remove).
  const [surface, setSurface] = useState<'marks' | 'roster'>('marks');
  const [rosterSearch, setRosterSearch] = useState('');
  const [rosterBusy, setRosterBusy] = useState<string | null>(null);
  /* The pupil a teacher has asked to remove, once we know what removing them
     would delete. Null until the preview comes back. */
  const [removeAsk, setRemoveAsk] = useState<{
    student: Student;
    entries: { term: string; hasMark: boolean; termMark: number | null; caMark: number | null; exam: unknown; grade: string; locked: boolean }[];
  } | null>(null);
  const [removeChecking, setRemoveChecking] = useState<string | null>(null);
  const [rosterMsg, setRosterMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [loadedEntries, setLoadedEntries] = useState<
    Record<string, SubjectMarkEntry>
  >({});
  const [docLoading, setDocLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<string | null>(null); // export-in-progress key
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });
  // Version gate (slice 1): a stale client is blocked from saving entirely.
  const [staleBlocked, setStaleBlocked] = useState(false);
  // Version gate (slice 2): surface the existing `dirty` flag into the global
  // registry so blanket auto-reload never fires while marks are unsaved.
  useDirtyFlag('markentry', dirty);

  // Deep-link from a "your comment was flagged" notification: preselect the
  // flagged subject + form + class, open comments, and highlight the pupil.
  useEffect(() => {
    if (flagFixDone.current || loading || classes.length === 0) return;
    let raw: string | null = null;
    try { raw = sessionStorage.getItem('lkc.commentFlagFix'); } catch { /* ignore */ }
    if (!raw) return;
    let d: { target?: string; classId?: string; subjectId?: string; studentId?: string };
    try { d = JSON.parse(raw); } catch { return; }
    // Subject comments (and marks) are fixed here; other targets are handled by
    // their own screens — leave the marker for them.
    if (d.target && d.target !== 'subjectComment' && d.target !== 'mark') return;
    flagFixDone.current = true;
    try { sessionStorage.removeItem('lkc.commentFlagFix'); } catch { /* ignore */ }
    const cls = classes.find((c) => c.id === d.classId);
    if (d.subjectId) setSubjectId(d.subjectId);
    if (cls?.form) setFormId(cls.form);
    if (d.classId) setClassFilter(d.classId);
    setCommentsOpen(true);
    if (d.studentId) setHighlightStudentId(d.studentId);
  }, [loading, classes]);

  // Clear the highlight after a few seconds.
  useEffect(() => {
    if (!highlightStudentId) return;
    const t = setTimeout(() => setHighlightStudentId(null), 5000);
    return () => clearTimeout(t);
  }, [highlightStudentId]);

  const months = getCaLabels(term, assessmentConfigs);
  const usesExam = termUsesExam(term);
  // June-intake pupils (AS/A2 June) are in THEIR Term 1 — CA-only, NO exam —
  // even though the school-global term (and their classmates in the same form,
  // the January intake) uses an exam. So the exam is gated per pupil, not per
  // form: a June pupil's row never shows or saves an exam.
  const juneStudentIds = useMemo(
    () => new Set(students.filter((s) => isJuneCohort(classes.find((c) => c.id === s.currentClass))).map((s) => s.id)),
    [students, classes],
  );
  const isJuneStu = (sid: string) => juneStudentIds.has(sid);

  const myClasses = useMemo(
    () => getAccessibleClasses(userData, classes, teachers, students),
    [userData, classes, teachers, students],
  );

  const linkedTeacher = useMemo(
    () => getLinkedTeacher(userData, teachers),
    [userData, teachers],
  );

  // ---- scope helpers -----------------------------------------------
  const subjectsOfferedInClass = useCallback(
    (cid: string) => {
      const ids = new Set<string>();
      students
        .filter((s) => s.currentClass === cid)
        .forEach((s) => (s.subjects ?? []).forEach((x) => ids.add(x)));
      return ids;
    },
    [students],
  );

  // For a LINKED teacher: may they open this subject in this class?
  // Either they teach it there, or they're the class teacher and the
  // subject is offered (other-teacher groups then appear read-only).
  const teacherOpensSubjectInClass = useCallback(
    (cid: string, cname: string, sid: string) => {
      if (!linkedTeacher) return false;
      if (teacherTeachesSubjectInClass(linkedTeacher, sid, cid, cname))
        return true;
      const isClassTeacherHere =
        linkedTeacher.classTeacherOf === cid ||
        linkedTeacher.assistantClassTeacherOf?.includes(cid);
      return !!isClassTeacherHere && subjectsOfferedInClass(cid).has(sid);
    },
    [linkedTeacher, subjectsOfferedInClass],
  );

  // Is class `c` in scope for subject `sid`? Linked => teacher rule;
  // otherwise (manager/admin) => any class that offers the subject.
  const classInScopeForSubject = useCallback(
    (c: SchoolClass, sid: string) =>
      linkedTeacher
        ? teacherOpensSubjectInClass(c.id, c.name, sid)
        : subjectsOfferedInClass(c.id).has(sid),
    [linkedTeacher, teacherOpensSubjectInClass, subjectsOfferedInClass],
  );

  // Subjects the user may pick.
  const pickableSubjects = useMemo(() => {
    const ids = new Set<string>();
    if (linkedTeacher) {
      for (const c of myClasses) {
        for (const sid of subjectsOfferedInClass(c.id)) {
          if (teacherOpensSubjectInClass(c.id, c.name, sid)) ids.add(sid);
        }
      }
    } else {
      // manager / admin — every subject offered to at least one student
      // in a class they can access.
      for (const c of myClasses) {
        subjectsOfferedInClass(c.id).forEach((x) => ids.add(x));
      }
    }
    return subjects
      .filter((s) => ids.has(s.id))
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }, [linkedTeacher, myClasses, subjects, subjectsOfferedInClass, teacherOpensSubjectInClass]);

  // Forms the chosen subject spans (sorted).
  const subjectForms = useMemo(() => {
    if (!subjectId) return [];
    const forms = new Set<string>();
    for (const c of myClasses) {
      if (classInScopeForSubject(c, subjectId)) forms.add(c.form);
    }
    return Array.from(forms).sort();
  }, [subjectId, myClasses, classInScopeForSubject]);

  // Classes for the chosen subject within the chosen form.
  const groupClasses = useMemo(() => {
    if (!subjectId || !formId) return [];
    return myClasses
      .filter((c) => c.form === formId && classInScopeForSubject(c, subjectId))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [subjectId, formId, myClasses, classInScopeForSubject]);

  const activeClasses = groupClasses;
  const showGrades = shouldShowGrades(formId || null);

  // Per-class roster, split into the user's editable group vs read-only
  // students (taught by another teacher, shown to a class teacher for
  // visibility). Empty classes are dropped.
  const groupedRoster: RosterGroup[] = useMemo(() => {
    if (!subjectId || activeClasses.length === 0) return [];
    return activeClasses
      .map((c) => {
        const inClass = students
          .filter(
            (s) =>
              s.currentClass === c.id && s.subjects?.includes(subjectId),
          )
          .filter((s) => canViewMarksFor(userData, s, subjectId, teachers));
        const ed = inClass
          .filter((s) => canEnterMarksFor(userData, s, subjectId, teachers))
          .sort((a, b) => a.fullName.localeCompare(b.fullName));
        const ro = inClass
          .filter((s) => !canEnterMarksFor(userData, s, subjectId, teachers))
          .sort((a, b) => a.fullName.localeCompare(b.fullName));
        return { cls: c, editable: ed, readonly: ro };
      })
      .filter((g) => g.editable.length + g.readonly.length > 0);
  }, [activeClasses, subjectId, students, userData, teachers]);

  const rosterStudents: Student[] = useMemo(
    () => groupedRoster.flatMap((g) => [...g.editable, ...g.readonly]),
    [groupedRoster],
  );

  // Keep the class sub-tab (§1.3) valid as the selection changes.
  useEffect(() => {
    if (classFilter !== 'all' && !groupedRoster.some((g) => g.cls.id === classFilter)) {
      setClassFilter('all');
    }
  }, [groupedRoster, classFilter]);

  const shownGroups = useMemo(
    () => (classFilter === 'all' ? groupedRoster : groupedRoster.filter((g) => g.cls.id === classFilter)),
    [groupedRoster, classFilter],
  );

  const hasSelection = !!subjectId && activeClasses.length > 0;
  const activeKey = activeClasses.map((c) => c.id).join(',');
  const rosterKey = rosterStudents.map((s) => s.id).join(',');
  const subjectName = subjects.find((s) => s.id === subjectId)?.name ?? '';
  const subjectScheme = schemeForSubject(
    subjects.find((s) => s.id === subjectId),
  );

  // ---- auto-select when there's only one sensible choice ------------
  useEffect(() => {
    if (!subjectId) {
      if (pickableSubjects.length === 1) setSubjectId(pickableSubjects[0].id);
      else if (formId) setFormId('');
      return;
    }
    if (formId && !subjectForms.includes(formId)) {
      setFormId(subjectForms.length === 1 ? subjectForms[0] : '');
    } else if (!formId && subjectForms.length === 1) {
      setFormId(subjectForms[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId, formId, pickableSubjects, subjectForms]);

  // ---- load (unified): one getDoc per active class, merged ----------
  useEffect(() => {
    if (!subjectId || activeClasses.length === 0) {
      setLoadedEntries({});
      setDraft({});
      return;
    }
    let cancelled = false;
    setDocLoading(true);
    setDraft({});
    Promise.all(
      activeClasses.map((c) =>
        getDoc(
          doc(db, COLLECTIONS.marks, `${year}-${term}-${c.id}-${subjectId}`),
        ),
      ),
    )
      .then((snaps) => {
        if (cancelled) return;
        // studentId is globally unique, so entries from different class
        // docs never collide — a flat merge keyed by studentId is safe.
        const merged: Record<string, SubjectMarkEntry> = {};
        for (const snap of snaps) {
          if (!snap.exists()) continue;
          const e =
            (snap.data().entries as Record<string, SubjectMarkEntry>) ?? {};
          Object.assign(merged, e);
        }
        setLoadedEntries(merged);
        const d: Draft = {};
        for (const s of rosterStudents) {
          const e = merged[s.id];
          d[s.id] = {
            ca: Object.fromEntries(
              months.map((m) => {
                const v = e?.ca?.[m];
                return [m, v === undefined || v === null ? '' : String(v)];
              }),
            ),
            exam:
              e?.exam === undefined || e?.exam === null ? '' : String(e.exam),
            comment: e?.comment ?? '',
          };
        }
        // C3 restore: if a stash for THIS exact context survived a reload,
        // overlay it on the freshly-loaded draft so unsaved marks are not lost.
        let restored = false;
        try {
          const raw = sessionStorage.getItem('lkc_markentry_draft');
          if (raw) {
            const saved = JSON.parse(raw) as {
              ctx?: { year: string; term: string; subjectId: string; formId: string };
              draft?: Draft;
            };
            if (
              saved.draft && saved.ctx &&
              saved.ctx.year === year && saved.ctx.term === term &&
              saved.ctx.subjectId === subjectId && saved.ctx.formId === formId
            ) {
              for (const [sid, row] of Object.entries(saved.draft)) d[sid] = row;
              restored = true;
            }
            sessionStorage.removeItem('lkc_markentry_draft');
          }
        } catch {
          // ignore malformed / unavailable stash
        }
        setDraft(d);
        setDirty(restored);
      })
      .catch((err) =>
        setMsg({ ok: false, text: `Load failed: ${err.message}` }),
      )
      .finally(() => !cancelled && setDocLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, subjectId, year, term, rosterKey]);

  // C3 draft-stash: while dirty, mirror the draft to sessionStorage keyed by
  // context, so ANY reload (version-gate banner, the slice-1 save-block modal,
  // or a manual browser reload) restores unsaved marks. Cleared when clean
  // (saved / freshly loaded). Keeping the stash continuously current means the
  // slice-1 modal and the banner both reload losslessly with NO change to the
  // slice-1 save-block itself.
  useEffect(() => {
    try {
      if (dirty) {
        sessionStorage.setItem(
          'lkc_markentry_draft',
          JSON.stringify({ ctx: { year, term, subjectId, formId }, draft }),
        );
      } else {
        sessionStorage.removeItem('lkc_markentry_draft');
      }
    } catch {
      // sessionStorage unavailable (private mode / quota) - stash is best-effort.
    }
  }, [dirty, draft, year, term, subjectId, formId]);

  const derive = useCallback(
    (sid: string) => {
      const row = draft[sid];
      if (!row) return null;
      const ca: Record<string, MaybeMark> = {};
      for (const m of months) {
        const v = row.ca[m] ?? '';
        if (v === '') continue;
        const n = normaliseMark(v);
        if (n !== '') ca[m] = n;
      }
      const june = isJuneStu(sid);
      const examVal: MaybeMark | undefined = (!usesExam || june)
        ? undefined
        : (row.exam ?? '') === ''
          ? ''
          : (normaliseMark(row.exam) as MaybeMark);
      // June pupils grade CA-only (their Term 1); everyone else per the term.
      return deriveMark(june ? 'Term1' : term, ca, examVal, subjectScheme);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, months, term, usesExam, subjectScheme, juneStudentIds],
  );

  function setCa(sid: string, month: string, value: string) {
    if (draft[sid]?.ca?.[month] === value) return;
    setDraft((d) => ({
      ...d,
      [sid]: { ...d[sid], ca: { ...d[sid].ca, [month]: value } },
    }));
    setDirty(true); touchAutoSave();
  }
  function setExam(sid: string, value: string) {
    if (draft[sid]?.exam === value) return;
    setDraft((d) => ({ ...d, [sid]: { ...d[sid], exam: value } }));
    setDirty(true); touchAutoSave();
  }
  function setComment(sid: string, value: string) {
    if (draft[sid]?.comment === value) return;
    setDraft((d) => ({ ...d, [sid]: { ...d[sid], comment: value } }));
    setDirty(true); touchAutoSave();
  }

  // §1.2 — template-based subject-comment generator, in place. Derives the
  // student's mark category from THIS subject's term mark, fills a gender-aware
  // template (§1.1), and drops it straight into the editable comment box. The
  // per-student seed advances on each click so "Generate again" rotates
  // through the phrasing variants rather than repeating one line.
  // Load the approved-comment bank: the general doc once, and the current
  // form's shard whenever the selected form changes.
  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, COLLECTIONS.commentTemplates, 'subjectTemplatesGeneral'))
      .then((snap) => { if (!cancelled) setGeneralTpls(snap.exists() ? (snap.data() as SubjectTemplatesGeneral) : {}); })
      .catch(() => { if (!cancelled) setGeneralTpls({}); });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!formId) { setFormTpls({}); return; }
    let cancelled = false;
    getDoc(doc(db, COLLECTIONS.commentTemplates, `subjectTemplatesByForm__${formId}`))
      .then((snap) => { if (!cancelled) setFormTpls(snap.exists() ? (snap.data() as SubjectTemplatesGeneral) : {}); })
      .catch(() => { if (!cancelled) setFormTpls({}); });
    return () => { cancelled = true; };
  }, [formId]);

  const [genSeed, setGenSeed] = useState<Record<string, number>>({});
  function generateComment(s: Student) {
    const d = derive(s.id);
    const mark = d?.termMark ?? 60;
    const cat = getMarkCategory(mark);
    const seed = genSeed[s.id] ?? 0;
    // Prefer the approved bank (form-specific → general), else built-in.
    const { templates } = resolveTemplates(cat, {
      formLevel: formId,
      subjectId,
      byForm: formId ? { [formId]: formTpls } : undefined,
      general: generalTpls,
    });
    const pool = templates.length ? templates : BUILT_IN_FALLBACKS[cat];
    setComment(s.id, suggestComment(s.fullName, cat, pool, seed, s.gender));
    setGenSeed((g) => ({ ...g, [s.id]: seed + 1 }));
  }

  // ---- #1 roster management (add/remove students on MY subject) -----
  // Scope: students in the SELECTED FORM (any class of that form). The
  // teacher may only touch the subject they have selected — one they teach
  // (pickableSubjects already enforces that). Adding assigns the subject to
  // the student and stamps this teacher as its owner; removing clears both.
  const formStudents = useMemo(() => {
    if (!formId) return [] as Student[];
    const formClassIds = new Set(classes.filter((c) => c.form === formId && !c.archived).map((c) => c.id));
    return students
      .filter((s) => formClassIds.has(s.currentClass))
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [classes, students, formId]);

  const rosterTaking = useMemo(
    () => formStudents.filter((s) => s.subjects?.includes(subjectId)),
    [formStudents, subjectId],
  );

  // Search-driven only (the teacher searches names; no free browse of the
  // whole form). Results exclude students already on the subject.
  const rosterAvailable = useMemo(() => {
    const q = rosterSearch.trim().toLowerCase();
    if (!q) return [] as Student[];
    return formStudents.filter(
      (s) => !s.subjects?.includes(subjectId) && s.fullName.toLowerCase().includes(q),
    );
  }, [formStudents, subjectId, rosterSearch]);

  /** Adding is a plain client write — it creates nothing that can be orphaned. */
  async function addToSubject(s: Student) {
    if (!linkedTeacher || !subjectId) return;
    setRosterBusy(s.id);
    setRosterMsg(null);
    try {
      await updateDoc(
        doc(db, COLLECTIONS.students, s.id),
        { subjects: arrayUnion(subjectId), [`subjectTeachers.${subjectId}`]: linkedTeacher.id },
      );
      auditFromContext(userData, 'student_subject_added', 'academic', {
        targetId: s.id, targetType: 'student', targetName: s.fullName,
        details: { subjectId, subjectName, teacherId: linkedTeacher.id, form: formId },
      });
      setRosterMsg({ ok: true, text: `Added ${s.fullName} to ${subjectName}.` });
      setRosterSearch('');
    } catch (e) {
      setRosterMsg({ ok: false, text: (e as Error).message });
    } finally {
      setRosterBusy(null);
    }
  }

  /**
   * REMOVING IS NOT A PLAIN WRITE, and cannot be.
   *
   * A report's subject list comes from the MARKS, not from the pupil's subject
   * list: assembleReport includes a subject if the marks document has an entry
   * keyed by that pupil. So taking the pupil off the subject without deleting
   * their entry leaves the subject printing on the report with nothing against
   * it — and once they are off the subject they vanish from this very screen,
   * so nobody can reach the entry to clear it.
   *
   * The entry cannot be deleted from here either. firestore.rules forbids
   * removing ANY key from a marks document's `entries` map, for a teacher and
   * an admin alike — that rule is what stops a bad write wiping a co-teacher's
   * whole set. Blanking the marks does not help: the KEY is what puts the
   * subject on the report, and a blanked entry keeps its key.
   *
   * So the deletion happens server-side, in the removeStudentFromSubject
   * callable, where the Admin SDK bypasses the rule. This asks first what
   * would be deleted, shows the teacher exactly that, and only calls the
   * removal once they have said yes.
   */
  async function askToRemove(s: Student) {
    if (!subjectId) return;
    setRemoveChecking(s.id);
    setRosterMsg(null);
    try {
      const preview = httpsCallable<
        { studentId: string; subjectId: string; year: string },
        { entries: NonNullable<typeof removeAsk>['entries'] }
      >(functions, 'previewSubjectRemoval');
      const res = await preview({ studentId: s.id, subjectId, year });
      setRemoveAsk({ student: s, entries: res.data?.entries ?? [] });
    } catch (e) {
      setRosterMsg({
        ok: false,
        text: `Could not check ${s.fullName}'s marks for ${subjectName}: ${(e as Error).message}. `
          + 'Removing has been stopped — try again, and tell the office if it keeps failing.',
      });
    } finally {
      setRemoveChecking(null);
    }
  }

  async function confirmRemove() {
    if (!removeAsk || !subjectId) return;
    const s = removeAsk.student;
    setRosterBusy(s.id);
    setRosterMsg(null);
    try {
      const remove = httpsCallable<
        { studentId: string; subjectId: string; year: string },
        { subjectRemoved: boolean; removedFrom: { term: string; hadMark: boolean }[] }
      >(functions, 'removeStudentFromSubject');
      const res = await remove({ studentId: s.id, subjectId, year });
      const cleared = res.data?.removedFrom ?? [];
      // The function audits server-side; this is the teacher-facing note.
      setRosterMsg({
        ok: true,
        text: cleared.length
          ? `Removed ${s.fullName} from ${subjectName}, and deleted their ${cleared.map((c) => c.term).join(' and ')} entr${cleared.length === 1 ? 'y' : 'ies'}.`
          : `Removed ${s.fullName} from ${subjectName}.`,
      });
      setRemoveAsk(null);
    } catch (e) {
      setRosterMsg({ ok: false, text: (e as Error).message });
    } finally {
      setRosterBusy(null);
    }
  }

  // ---- exports (CSV / PDF; per class or whole form) -----------------
  const orderedStudents = (g: RosterGroup) =>
    [...g.editable.map((s) => ({ s, ro: false })), ...g.readonly.map((s) => ({ s, ro: true }))];

  function toPdfRow(s: Student, ro: boolean, no: number): MarksPdfRow {
    const dr = draft[s.id];
    const d = derive(s.id);
    return {
      no,
      name: s.fullName,
      ca: Object.fromEntries(months.map((m) => [m, disp(dr?.ca?.[m])])),
      caMark: disp(d?.caMark),
      exam: usesExam ? disp(dr?.exam) : undefined,
      termMark: disp(d?.termMark),
      grade: d?.reportGrade ?? '-',
      readOnly: ro,
    };
  }

  async function exportPdf(groups: RosterGroup[], scopeLabel: string) {
    if (groups.length === 0) return;
    const key = `pdf:${scopeLabel}`;
    setBusy(key);
    try {
      const logo = await loadLogoDataUrl();
      const pdfClasses: MarksPdfClass[] = groups.map((g) => ({
        className: g.cls.name,
        rows: orderedStudents(g).map((it, i) => toPdfRow(it.s, it.ro, i + 1)),
      }));
      buildMarksGridPdf({
        subjectName,
        termLabel: termLabel(term),
        year,
        months,
        usesExam,
        showGrades,
        classes: pdfClasses,
        formLabel: formLabel(formId),
        logoDataUrl: logo,
        generatedBy: userData?.displayName,
      }).save(`marks_${slug(scopeLabel)}_${slug(subjectName)}_${term}_${year}.pdf`);
    } catch (e) {
      setMsg({ ok: false, text: `PDF export failed: ${(e as Error).message}` });
    } finally {
      setBusy(null);
    }
  }

  function buildCsv(groups: RosterGroup[]): string {
    const monthCols = months.map((m) =>
      isMonthLocked(m) ? `${m} (LOCKED)` : m,
    );
    const header = [
      'Class',
      'No',
      'Student Name',
      'Gender',
      ...monthCols,
      ...(usesExam ? ['Exam'] : []),
      'CA (derived)',
      'Term (derived)',
      ...(showGrades ? ['Grade (derived)'] : []),
      'Comment (optional)',
    ];
    const lines: string[] = [];
    for (const g of groups) {
      orderedStudents(g).forEach((it, i) => {
        const dr = draft[it.s.id];
        const d = derive(it.s.id);
        const cells = [
          g.cls.name,
          String(i + 1),
          it.s.fullName + (it.ro ? ' (read-only)' : ''),
          it.s.gender ?? '',
          ...months.map((m) => disp(dr?.ca?.[m])),
          ...(usesExam ? [disp(dr?.exam)] : []),
          disp(d?.caMark),
          disp(d?.termMark),
          ...(showGrades ? [d?.reportGrade ?? '-'] : []),
          (dr?.comment ?? '').replace(/[\r\n,]/g, ' '),
        ];
        lines.push(cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','));
      });
    }
    const meta = [
      `# ${SCHOOL.shortName} Mark Sheet — ${subjectName} — ${termLabel(term)} ${year}`,
      `# Editable on upload: CA months (not LOCKED), Exam, Comment.`,
      `# Class + Student Name route each row back. (derived) and (read-only) columns are ignored.`,
      '',
    ];
    return [...meta, header.join(','), ...lines].join('\r\n');
  }

  function downloadCsv(groups: RosterGroup[], scopeLabel: string) {
    if (groups.length === 0) return;
    const csv = buildCsv(groups);
    const blob = new Blob(['\uFEFF' + csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `marks_${slug(scopeLabel)}_${slug(subjectName)}_${term}_${year}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // ---- CSV import ---------------------------------------------------
  const csvFileRef = useRef<HTMLInputElement>(null);

  function splitCsvLine(line: string): string[] {
    const out: string[] = [];
    let cur = '';
    let q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (q && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else q = !q;
      } else if (ch === ',' && !q) {
        out.push(cur);
        cur = '';
      } else cur += ch;
    }
    out.push(cur);
    return out.map((x) => x.trim());
  }

  const normName = (s: string) =>
    s.toLowerCase().replace(/\(read-only\)/g, '').replace(/\s+/g, ' ').trim();
  const className = (cid: string) =>
    classes.find((c) => c.id === cid)?.name ?? cid;

  function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (csvFileRef.current) csvFileRef.current.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result);
        const lines = text
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter((l) => l && !l.startsWith('#'));
        if (lines.length < 2) {
          setMsg({ ok: false, text: 'CSV has no data rows.' });
          return;
        }
        const head = splitCsvLine(lines[0]).map((h) => h.toLowerCase());
        const nameIdx = head.findIndex((h) => h.includes('student'));
        if (nameIdx === -1) {
          setMsg({
            ok: false,
            text: 'CSV missing a "Student Name" column. Use an exported sheet.',
          });
          return;
        }
        const classIdx = head.findIndex((h) => h.trim() === 'class');
        const colTarget: (
          | { kind: 'month'; month: string }
          | { kind: 'exam' }
          | { kind: 'comment' }
          | null
        )[] = head.map((h) => {
          if (h.includes('derived')) return null; // CA/Term/Grade summaries
          if (h.includes('comment')) return { kind: 'comment' };
          if (h.includes('exam')) return { kind: 'exam' };
          const hit = months.find((m) => h.startsWith(m.toLowerCase()));
          if (hit) return { kind: 'month', month: hit };
          return null;
        });

        // Editable students across the whole current selection.
        const editableStudents = rosterStudents.filter((s) =>
          canEnterMarksFor(userData, s, subjectId, teachers),
        );
        const byClassName = new Map<string, Student>();
        const byName = new Map<string, Student | null>(); // null => ambiguous
        for (const s of editableStudents) {
          byClassName.set(
            `${normName(className(s.currentClass))}|${normName(s.fullName)}`,
            s,
          );
          const k = normName(s.fullName);
          byName.set(k, byName.has(k) ? null : s);
        }

        let applied = 0;
        let skippedLocked = 0;
        let unmatched = 0;
        let ambiguous = 0;
        for (const line of lines.slice(1)) {
          const cells = splitCsvLine(line);
          const nm = normName(cells[nameIdx] ?? '');
          if (!nm) continue;
          let stu: Student | null | undefined;
          if (classIdx >= 0 && cells[classIdx]) {
            stu = byClassName.get(`${normName(cells[classIdx])}|${nm}`);
          } else {
            stu = byName.get(nm);
            if (stu === null) {
              ambiguous++;
              continue;
            }
          }
          if (!stu) {
            unmatched++;
            continue;
          }
          cells.forEach((raw, ci) => {
            const t = colTarget[ci];
            if (!t) return;
            const val = raw.trim();
            if (t.kind === 'comment') {
              if (val) setComment(stu!.id, val);
            } else if (t.kind === 'exam') {
              if (usesExam && val) setExam(stu!.id, val);
            } else if (t.kind === 'month') {
              if (isMonthLocked(t.month)) {
                if (val) skippedLocked++;
                return;
              }
              if (val) setCa(stu!.id, t.month, val);
            }
          });
          applied++;
        }
        const parts = [`Applied ${applied} row(s) to the grid`];
        if (skippedLocked) parts.push(`${skippedLocked} locked-month value(s) ignored`);
        if (ambiguous)
          parts.push(`${ambiguous} duplicate name(s) skipped — use a sheet with a Class column`);
        if (unmatched) parts.push(`${unmatched} name(s) not matched / not yours`);
        parts.push('— review, then Save.');
        setMsg({ ok: applied > 0, text: parts.join(' · ') });
      } catch {
        setMsg({ ok: false, text: 'Could not read that CSV.' });
      }
    };
    reader.readAsText(file);
  }

  // ---- keyboard nav -------------------------------------------------
  const gridRef = useRef<HTMLDivElement>(null);
  function onKeyNav(e: React.KeyboardEvent, rowIdx: number, col: string) {
    if (!['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
    e.preventDefault();
    const dir = e.key === 'ArrowUp' ? -1 : 1;
    const el = gridRef.current?.querySelector<HTMLInputElement>(
      `[data-cell="${rowIdx + dir}-${col}"]`,
    );
    el?.focus();
    el?.select();
  }

  // ---- save (unified): one reportMarks doc per active class ---------
  async function save() {
    // VERSION GATE (hard block): a stale client must never write - the
    // pre-fix bundle rewrites the whole entries map and clobbers co-teachers'
    // marks. One check up front aborts the ENTIRE save (which writes per-class
    // in the loop below) before any setDoc. Fail-open on unknown serverBuild -
    // see isClientStale.
    if (isClientStale(serverBuild)) {
      setStaleBlocked(true);
      return;
    }
    if (!editable) {
      setMsg({ ok: false, text: 'This term is locked. Admin only.' });
      return;
    }
    setSaving(true);
    try {
      const newLoaded: Record<string, SubjectMarkEntry> = { ...loadedEntries };
      const writtenClasses: string[] = [];
      // Students where the teacher typed something that ISN'T a valid mark
      // (normaliseMark drops non-integers like "67.5" or a typo "6O" to blank).
      // Collected so the save warns instead of silently losing the value.
      const droppedNames: string[] = [];

      for (const c of activeClasses) {
        const classStudents = rosterStudents.filter(
          (s) => s.currentClass === c.id,
        );
        const editableHere = classStudents.filter((s) =>
          canEnterMarksFor(userData, s, subjectId, teachers),
        );
        if (editableHere.length === 0) continue;

        // Write ONLY the students this teacher owns (editableHere), never a
        // rebuilt full-class map. With setDoc({merge:true}) Firestore deep-
        // merges the entries map, so every co-teacher's studentId key that is
        // absent from this payload is preserved untouched. This is what closes
        // the concurrent-save race: you cannot clobber another teacher's
        // students if you never write their keys. (Previously this seeded the
        // payload from the grid-open `loadedEntries` snapshot, so a later save
        // re-wrote co-teachers' students from stale state.)
        const entries: Record<string, SubjectMarkEntry> = {};

        for (const s of editableHere) {
          const row = draft[s.id];
          if (!row) continue;
          const ca: Record<string, MaybeMark> = {};
          for (const m of months) {
            if (isMonthLocked(m)) {
              const prior = loadedEntries[s.id]?.ca?.[m];
              if (prior !== undefined && prior !== '')
                ca[m] = prior as MaybeMark;
              continue;
            }
            const v = row.ca[m] ?? '';
            if (v === '') continue;
            const n = normaliseMark(v);
            if (n !== '') ca[m] = n;
          }
          // June pupils (AS/A2 June) are CA-only this term — never an exam.
          const june = isJuneStu(s.id);
          const examVal: MaybeMark | undefined = (!usesExam || june)
            ? undefined
            : (row.exam ?? '') === ''
              ? ''
              : (normaliseMark(row.exam) as MaybeMark);

          // Detect typed-but-invalid input BEFORE the blank-skip guard, so a
          // teacher who typed "67.5" for a student is warned rather than having
          // it vanish. normaliseMark turns anything that isn't 0–100 or ABS
          // into '' — that's what silently dropped these marks.
          const caDropped = months.some((m) => {
            if (isMonthLocked(m)) return false;
            const raw = String(row.ca[m] ?? '').trim();
            return raw !== '' && normaliseMark(raw) === '';
          });
          const examDropped =
            usesExam && !june && String(row.exam ?? '').trim() !== '' && normaliseMark(row.exam) === '';
          if (caDropped || examDropped) droppedNames.push(s.fullName);

          // GUARD: don't shadow absence with an empty entry.
          const hasAnyCa = Object.keys(ca).length > 0;
          const hasExam = usesExam && !june && examVal !== undefined && examVal !== '';
          const hasComment = (row.comment ?? '').trim().length > 0;
          const hadPrior = !!loadedEntries[s.id];
          if (!hasAnyCa && !hasExam && !hasComment && !hadPrior) continue;

          const d = deriveMark(june ? 'Term1' : term, ca, examVal, subjectScheme);
          const entry: SubjectMarkEntry = {
            ca,
            caMark: d.caMark ?? null,
            termMark: d.termMark ?? null,
            reportGrade: d.reportGrade ?? null,
            comment: sanitizeComment(row.comment, 600),
            updatedAt: new Date().toISOString(),
          } as SubjectMarkEntry;
          if (usesExam && !june && examVal !== undefined && examVal !== '') {
            (entry as { exam?: MaybeMark }).exam = examVal;
          }
          if (userData?.uid) {
            (entry as { updatedBy?: string }).updatedBy = userData.uid;
          }
          // Stamp the crediting teacher from the SAME live link that drives
          // the grid's owner label (s.subjectTeachers[subjectId]). Omitted
          // when there's no current teacher, so read paths fall back to the
          // live link. Once stamped, a later Teacher Swap won't rewrite it.
          const ownerId = s.subjectTeachers?.[subjectId];
          if (ownerId) {
            (entry as { teacherId?: string }).teacherId = ownerId;
          }
          entries[s.id] = entry;
        }

        // WIPE GUARD (13.7): never write an EMPTY entries map. setDoc(merge)
        // with `entries: {}` can REPLACE the field rather than merge nothing,
        // erasing the whole class doc — including every co-teacher's marks. An
        // empty payload means there is nothing to save for this class → skip.
        if (Object.keys(entries).length === 0) continue;

        const id = `${year}-${term}-${c.id}-${subjectId}`;
        // The @/lib/firebase setDoc wrapper strips undefined values before
        // writing (e.g. updatedBy when there's no uid), so no manual strip.
        const payload = {
          id,
          year,
          term,
          classId: c.id,
          subjectId,
          entries,
          updatedAt: new Date().toISOString(),
          updatedBy: userData?.uid,
        };
        await setDoc(
          doc(db, COLLECTIONS.marks, id),
          payload as Record<string, unknown>,
          { merge: true },
        );

        Object.assign(newLoaded, entries);
        writtenClasses.push(c.id);
        auditFromContext(userData, 'marks_saved', 'academic', {
          targetId: c.id,
          targetType: 'marks',
          targetName: className(c.id),
          details: {
            year,
            term,
            subjectId,
            studentsUpdated: Object.keys(entries).length,
          },
        });
      }

      const dropped = [...new Set(droppedNames)];
      const dropWarning = dropped.length
        ? ` ${dropped.length} mark${dropped.length === 1 ? '' : 's'} were NOT saved because they aren't a whole number 0–100 (or ABS) — check and re-enter: ${dropped.join(', ')}.`
        : '';

      if (writtenClasses.length === 0) {
        setSaving(false);
        setSaveModal({
          show: true,
          status: 'error',
          message: dropped.length
            ? `No marks saved.${dropWarning}`
            : 'Nothing to save — no rows you can edit.',
        });
        return;
      }

      setLoadedEntries(newLoaded);
      setDirty(false);
      setSaveModal({
        show: true,
        status: dropped.length ? 'error' : 'success',
        message:
          (writtenClasses.length > 1
            ? `Marks saved across ${writtenClasses.length} classes.`
            : 'Marks saved successfully.') + dropWarning,
      });
    } catch (e) {
      // A PERMISSION_DENIED here means the anti-clobber rule rejected a write that
      // would have dropped other teachers' entries (an old-build full-map save).
      // The marks were NOT lost — tell the user plainly and point at the fix.
      const err = e as { code?: string; message?: string };
      const denied = err?.code === 'permission-denied'
        || /permission[-_ ]?denied/i.test(err?.message ?? '');
      setSaveModal({
        show: true,
        status: 'error',
        message: denied
          ? "This save was blocked to protect other teachers' entries from being overwritten. "
            + 'Your marks were NOT lost — please fully close and reopen the app (or hard-refresh the page), '
            + 'then re-enter and save. If it keeps happening, contact Mr Makoni.'
          : (e as Error).message,
      });
    } finally {
      setSaving(false);
    }
  }

  const { touch: touchAutoSave } = useAutoSave(
    async () => { if (dirty && hasSelection) await save(); },
    300000,
    dirty && hasSelection,
  );

  if (loading) return <Spinner label="Loading…" />;

  const multiClass = groupedRoster.length > 1;

  // ---- #1 roster-management surface -------------------------------
  function renderRosterManager() {
    const myId = linkedTeacher?.id;
    return (
      <Card className="p-4 sm:p-5 space-y-5">
        <div className="flex items-start gap-2 text-xs text-gray-500">
          <Users size={15} className="shrink-0 mt-0.5" />
          <span>
            Add or remove students on <strong>{subjectName}</strong> for{' '}
            <strong>{formLabel(formId)}</strong>. This changes each student's subject list and
            assigns you as their {subjectName} teacher. You can only change this one subject.
          </span>
        </div>

        {rosterMsg && (
          <div
            className={`rounded-xl px-4 py-2.5 text-sm ${
              rosterMsg.ok
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
            }`}
          >
            {rosterMsg.text}
          </div>
        )}

        {/* On my list */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
            On your list <span className="font-normal text-gray-400">({rosterTaking.length})</span>
          </h3>
          {rosterTaking.length === 0 ? (
            <p className="text-sm text-gray-400 py-3">No students on {subjectName} in this form yet.</p>
          ) : (
            <div className="space-y-1.5">
              {rosterTaking.map((s) => {
                const ownerId = s.subjectTeachers?.[subjectId];
                const ownedByOther = !!ownerId && ownerId !== myId;
                const ownerName = ownedByOther
                  ? teachers.find((t) => t.id === ownerId)?.displayName ?? 'another teacher'
                  : '';
                return (
                  <div
                    key={s.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-gray-800 dark:text-gray-100 truncate">
                        {s.fullName}
                      </p>
                      <p className="text-[11px] text-gray-400">
                        {className(s.currentClass)}
                        {ownedByOther && ` · in ${ownerName}'s set`}
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={rosterBusy === s.id || removeChecking === s.id || ownedByOther}
                      onClick={() => askToRemove(s)}
                      title={
                        ownedByOther
                          ? `Assigned to ${ownerName} — only they can remove`
                          : `Remove from ${subjectName}`
                      }
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed dark:bg-red-900/20 dark:text-red-300"
                    >
                      <UserMinus size={13} />
                      {rosterBusy === s.id || removeChecking === s.id ? '…' : 'Remove'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Add by search */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Add a student</h3>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={rosterSearch}
              onChange={(e) => setRosterSearch(e.target.value)}
              placeholder={`Search ${formLabel(formId)} students by name…`}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-primary/15 focus:border-primary transition-all text-sm"
            />
          </div>
          {rosterSearch.trim() === '' ? (
            <p className="text-xs text-gray-400 mt-2">Type a name to find students not yet on {subjectName}.</p>
          ) : rosterAvailable.length === 0 ? (
            <p className="text-sm text-gray-400 mt-2 py-2">No matching students in {formLabel(formId)} who aren't already on {subjectName}.</p>
          ) : (
            <div className="space-y-1.5 mt-2">
              {rosterAvailable.slice(0, 20).map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-gray-800 dark:text-gray-100 truncate">{s.fullName}</p>
                    <p className="text-[11px] text-gray-400">{className(s.currentClass)}</p>
                  </div>
                  <button
                    type="button"
                    disabled={rosterBusy === s.id}
                    onClick={() => addToSubject(s)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 disabled:opacity-40"
                  >
                    <UserPlus size={13} />
                    {rosterBusy === s.id ? '…' : 'Add'}
                  </button>
                </div>
              ))}
              {rosterAvailable.length > 20 && (
                <p className="text-[11px] text-gray-400 pt-1">Showing first 20 — refine your search.</p>
              )}
            </div>
          )}
        </div>

        {removeAsk && <RemoveConfirm />}
      </Card>
    );
  }

  /**
   * What removing this pupil will delete, said before it happens.
   *
   * The teacher is told the marks go too, because they do — and because the
   * alternative they would otherwise reach for (blank the marks, then remove)
   * does not work: the entry's KEY is what puts the subject on the report, and
   * blanking keeps the key. That is how 111 orphaned entries accumulated.
   */
  function RemoveConfirm() {
    if (!removeAsk) return null;
    const { student, entries } = removeAsk;
    const marked = entries.filter((e) => e.hasMark);
    const blank = entries.filter((e) => !e.hasMark);
    const locked = entries.filter((e) => e.locked);
    const busy = rosterBusy === student.id;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
        <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-500 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 dark:text-white">
                Remove {student.fullName} from {subjectName}?
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {className(student.currentClass)} · {year}
              </p>
            </div>
          </div>

          <div className="px-5 py-4 space-y-3 text-sm text-gray-700 dark:text-gray-200">
            {entries.length === 0 ? (
              <p>No marks have been entered for {student.fullName} in {subjectName}. Nothing else will be deleted.</p>
            ) : (
              <>
                <p className="font-medium">
                  This will also delete {entries.length === 1 ? 'their entry' : `their ${entries.length} entries`} for this subject:
                </p>
                <ul className="space-y-1">
                  {entries.map((e) => (
                    <li key={e.term} className="flex items-center justify-between gap-3 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-900/40">
                      <span className="font-medium">{e.term}</span>
                      <span className={e.hasMark ? 'font-bold text-gray-900 dark:text-white tabular-nums' : 'text-gray-400'}>
                        {e.hasMark
                          ? `${e.termMark ?? e.caMark ?? e.exam}${e.grade ? ` · ${e.grade}` : ''}`
                          : 'no mark (absent / blank)'}
                        {e.locked && <span className="ml-2 text-[10px] font-bold uppercase text-red-600">term locked</span>}
                      </span>
                    </li>
                  ))}
                </ul>

                {marked.length > 0 && (
                  <p className="text-amber-700 dark:text-amber-300 text-xs">
                    {marked.length === 1 ? 'One of these is a real mark' : `${marked.length} of these are real marks`}. If {student.fullName} still
                    takes {subjectName}, cancel — it is the subject list that is wrong, not the mark.
                  </p>
                )}
                {blank.length > 0 && marked.length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Nothing is lost — there is no result in {blank.length === 1 ? 'it' : 'them'}. But the entry has to go, or
                    {' '}{subjectName} keeps appearing on the report with nothing against it.
                  </p>
                )}
                {locked.length > 0 && (
                  <p className="text-red-600 dark:text-red-400 text-xs font-medium">
                    A locked term cannot be changed. This removal will be refused — ask an administrator to unlock it first.
                  </p>
                )}
              </>
            )}
            <p className="text-[11px] text-gray-400">
              You will not be able to undo this from here: once removed, {student.fullName} no longer appears on your
              {' '}{subjectName} list. The deleted marks are kept in the audit log.
            </p>
          </div>

          <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setRemoveAsk(null)}
              disabled={busy}
              className="px-4 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-600 text-sm font-semibold hover:border-gray-300 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmRemove}
              disabled={busy || locked.length > 0}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              <UserMinus size={14} />
              {busy ? 'Removing…' : entries.length ? 'Remove and delete marks' : 'Remove'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- single student row (shared by editable + read-only lists) ----
  function renderRow(s: Student, idx: number) {
    const row = draft[s.id];
    if (!row) return null;
    const d = derive(s.id);
    const gradeSrc = d?.gradeSource ?? null;
    const gColor = gradeColor(d?.reportGrade ?? '-');
    const hasComment = !!row.comment && row.comment.trim().length > 0;
    const mayEditRow =
      editable && canEnterMarksFor(userData, s, subjectId, teachers);
    // Open inline when the global toggle is on (editable rows) or this row was
    // opened explicitly (e.g. a read-only row a teacher clicked to view).
    const isOpen = openComment === s.id || (commentsOpen && mayEditRow);
    const ownerId = s.subjectTeachers?.[subjectId];
    const ownerName = ownerId
      ? (teachers.find((t) => t.id === ownerId)?.displayName ?? 'another teacher')
      : 'another teacher';
    return (
      <div
        key={s.id}
        ref={
          s.id === highlightStudentId
            ? (el) => {
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            : undefined
        }
        className={`rounded-2xl border transition-all ${
          s.id === highlightStudentId ? 'ring-4 ring-amber-400/70 animate-pulse ' : ''
        }${
          mayEditRow
            ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600'
            : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700'
        }`}
      >
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-4">
          {/* Student identity */}
          <div className="lg:w-56 shrink-0">
            <div className="flex items-center gap-3">
              {/* The pupil's photo, so a teacher can put a face to the name;
                  initials when there is none. Dimmed on a row they cannot edit. */}
              <StudentAvatar student={s} size="md" className={mayEditRow ? '' : 'opacity-60 grayscale'} />
              <div className="min-w-0">
                <p
                  className={`font-semibold truncate ${
                    mayEditRow
                      ? 'text-gray-800 dark:text-gray-100'
                      : 'text-gray-500'
                  }`}
                >
                  {s.fullName}
                </p>
                {!mayEditRow && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-400"
                    title={`Taught by ${ownerName} — read-only for you`}
                  >
                    <Lock size={9} />
                    {ownerName}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* CA month inputs */}
          <div className="flex flex-wrap items-end gap-2 flex-1">
            {months.map((m) => {
              const mLocked = isMonthLocked(m);
              return (
                <div key={m} className="text-center">
                  <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                    {m.slice(0, 3)}
                  </label>
                  <input
                    data-cell={`${idx}-${m}`}
                    value={row.ca[m] ?? ''}
                    disabled={!mayEditRow || mLocked}
                    title={
                      mLocked
                        ? `${m} is locked — its deadline has passed`
                        : undefined
                    }
                    onChange={(e) => setCa(s.id, m, e.target.value)}
                    onKeyDown={(e) => onKeyNav(e, idx, m)}
                    onFocus={(e) => e.currentTarget.select()}
                    className={`w-16 h-12 text-center font-bold text-lg rounded-xl border-2 focus:outline-none focus:ring-4 focus:ring-primary/15 focus:border-primary transition-all disabled:opacity-60 ${
                      mLocked
                        ? 'border-amber-300 bg-amber-50/70 dark:border-amber-700 dark:bg-amber-900/20 cursor-not-allowed'
                        : 'border-gray-200 dark:border-gray-600 dark:bg-gray-900'
                    }`}
                    placeholder={mLocked ? '🔒' : '–'}
                  />
                </div>
              );
            })}

            {/* CA / Term result */}
            <div className="text-center">
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-primary/70 mb-1">
                CA
              </label>
              <div className="w-16 h-12 flex items-center justify-center rounded-xl bg-primary/5 border-2 border-primary/15 font-bold text-lg text-primary">
                {d?.caMark ?? '–'}
              </div>
            </div>

            {usesExam && !isJuneStu(s.id) && (
              <div className="text-center">
                <label className="block text-[10px] font-semibold uppercase tracking-wide text-secondary mb-1">
                  Exam
                </label>
                <input
                  data-cell={`${idx}-exam`}
                  value={row.exam ?? ''}
                  disabled={!mayEditRow}
                  onChange={(e) => setExam(s.id, e.target.value)}
                  onKeyDown={(e) => onKeyNav(e, idx, 'exam')}
                  onFocus={(e) => e.currentTarget.select()}
                  className="w-16 h-12 text-center font-bold text-lg rounded-xl border-2 border-secondary/30 bg-secondary/5 focus:outline-none focus:ring-4 focus:ring-secondary/15 focus:border-secondary transition-all disabled:opacity-60 dark:bg-gray-900"
                  placeholder="–"
                />
              </div>
            )}
          </div>

          {/* Grade badge + comment toggle */}
          <div className="flex items-center gap-3 shrink-0">
            {showGrades && (
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-extrabold text-lg shadow-sm"
                style={{
                  backgroundColor: isBlank(gradeSrc as MaybeMark)
                    ? '#cbd5e1'
                    : rgb(gColor),
                }}
                title="Report grade"
              >
                {d?.reportGrade ?? '-'}
              </div>
            )}
            {commentsOpen && mayEditRow ? (
              // Field is already open inline — show status only, no toggle.
              <div
                className={`flex items-center gap-1.5 px-3 h-12 rounded-xl border-2 text-xs font-semibold ${
                  hasComment
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'border-gray-200 dark:border-gray-600 text-gray-400'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${hasComment ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
                {hasComment ? 'Comment' : 'Comment'}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setOpenComment(isOpen ? null : s.id)}
                title={
                  hasComment
                    ? 'Comment entered — click to edit'
                    : 'No comment — click to add'
                }
                className={`flex items-center gap-1.5 px-3 h-12 rounded-xl border-2 text-xs font-semibold transition-all ${
                  hasComment
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    hasComment ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
                {hasComment ? 'Comment' : 'Add'}
                {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            )}
          </div>
        </div>

        {isOpen && (
          <div className="px-4 pb-4 -mt-1 space-y-2">
            <textarea
              value={row.comment ?? ''}
              disabled={!mayEditRow}
              onChange={(e) => setComment(s.id, e.target.value)}
              rows={2}
              placeholder={`Optional subject comment for ${s.fullName}…`}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-primary/15 focus:border-primary transition-all disabled:opacity-60 text-sm"
            />
            {mayEditRow && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => generateComment(s)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 transition-all"
                >
                  <Sparkles size={13} />
                  {hasComment ? 'Generate again' : 'Generate'}
                </button>
                <span className="text-[11px] text-gray-400">
                  Template-based, gender-aware — review &amp; edit before saving.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Mark Entry"
        subtitle={`${termLabel(term)} ${year} · ${
          usesExam
            ? 'CA marks + separate exam (grade from exam)'
            : 'monthly CA (term mark = average, absent ignored)'
        }`}
        actions={
          hasSelection && (
            <div className="flex items-center gap-2">
              <input
                ref={csvFileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleUpload}
              />
              <button
                onClick={() => setCommentsOpen((v) => !v)}
                title={commentsOpen ? 'Hide the inline comment fields' : 'Show comment fields inline for faster entry'}
                className={`flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-xl border transition-all ${
                  commentsOpen
                    ? 'border-primary/30 bg-primary/10 text-primary'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <MessageSquare size={15} /> {commentsOpen ? 'Comments shown' : 'Comments hidden'}
              </button>
              <button
                onClick={() => downloadCsv(groupedRoster, formLabel(formId) || 'form')}
                title="Export the whole form to CSV"
                className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <FileSpreadsheet size={15} /> {multiClass ? 'Form CSV' : 'CSV'}
              </button>
              <button
                onClick={() => exportPdf(groupedRoster, formLabel(formId) || 'form')}
                disabled={busy !== null}
                title="Export the whole form to PDF"
                className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                <FileText size={15} /> {multiClass ? 'Form PDF' : 'PDF'}
              </button>
              <button
                onClick={() => csvFileRef.current?.click()}
                disabled={!editable}
                title="Import a filled-in CSV (you still review &amp; Save)"
                className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                <Upload size={15} /> Import
              </button>
              <Button
                icon={<Save size={16} />}
                loading={saving}
                disabled={!editable || !dirty}
                onClick={save}
              >
                Save marks
              </Button>
            </div>
          )
        }
      />

      {!editable && (
        <div className="mb-4 flex items-center gap-2 text-sm rounded-xl px-3 py-2.5 border bg-amber-50 text-amber-700 border-amber-200">
          <Lock size={15} />
          {termLabel(term)} {year} is {termState}. Only an administrator can
          edit marks.
        </div>
      )}
      {msg && (
        <div
          className={`mb-4 text-sm rounded-xl px-3 py-2.5 border flex items-center gap-2 ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          {msg.ok ? <Check size={15} /> : <Info size={15} />}
          {msg.text}
        </div>
      )}

      {/* ---- selection ---- */}
      <Card className="p-4 mb-4">
        {pickableSubjects.length === 0 ? (
          <p className="text-sm text-gray-500">
            You don't have any subjects to enter marks for yet. If you think
            this is wrong, ask an administrator to check your teaching
            assignments.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Subject</label>
              {pickableSubjects.length > 1 ? (
                <select
                  value={subjectId}
                  onChange={(e) => {
                    setSubjectId(e.target.value);
                    setFormId('');
                  }}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                >
                  <option value="">— select subject —</option>
                  {pickableSubjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 font-medium">
                  {subjectName || pickableSubjects[0]?.name}
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Form</label>
              {!subjectId ? (
                <div className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 text-gray-400">
                  — select a subject first —
                </div>
              ) : subjectForms.length > 1 ? (
                <select
                  value={formId}
                  onChange={(e) => setFormId(e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                >
                  <option value="">— select form —</option>
                  {subjectForms.map((f) => (
                    <option key={f} value={f}>
                      {formLabel(f)}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 font-medium">
                  {formId ? formLabel(formId) : '—'}
                </div>
              )}
            </div>
          </div>
        )}

        {hasSelection && multiClass && (
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
            <Users size={14} />
            {subjectName} · {formLabel(formId)} · {groupClasses.length} classes
            on one page — one Save persists them all.
          </div>
        )}
      </Card>

      {/* #1 — surface switch: enter marks vs manage the subject roster.
          Only linked teachers manage assignments; managers use Setup. */}
      {hasSelection && linkedTeacher && (
        <div className="flex gap-1 p-1 mb-4 rounded-xl bg-gray-100 dark:bg-gray-800">
          <button
            type="button"
            onClick={() => setSurface('marks')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${surface === 'marks' ? 'seg-on' : 'seg-off'}`}
          >
            <FileSpreadsheet size={15} /> Enter marks
          </button>
          <button
            type="button"
            onClick={() => setSurface('roster')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${surface === 'roster' ? 'seg-on' : 'seg-off'}`}
          >
            <Users size={15} /> Class list
          </button>
        </div>
      )}

      {hasSelection && surface === 'roster' && renderRosterManager()}

      {hasSelection && surface === 'marks' && (
        <Card className="overflow-hidden">
          {docLoading ? (
            <Spinner label="Loading marks…" />
          ) : rosterStudents.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-12">
              No students you can enter marks for in this selection.
            </p>
          ) : (
            <div ref={gridRef} className="p-3 sm:p-4 space-y-2.5">
              {multiClass && (
                <div className="flex flex-wrap gap-1 p-1 mb-1 rounded-xl bg-gray-100 dark:bg-gray-800">
                  <button
                    onClick={() => setClassFilter('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${classFilter === 'all' ? 'seg-on' : 'seg-off'}`}
                  >
                    All classes
                  </button>
                  {groupedRoster.map((g) => (
                    <button
                      key={g.cls.id}
                      onClick={() => setClassFilter(g.cls.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${classFilter === g.cls.id ? 'seg-on' : 'seg-off'}`}
                    >
                      {g.cls.name}
                    </button>
                  ))}
                </div>
              )}
              {(() => {
                let flatIdx = -1;
                return shownGroups.map((group) => (
                  <div key={group.cls.id} className="space-y-2.5">
                    {multiClass && (
                      <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-primary/5 border border-primary/10 mt-1">
                        <span className="text-sm font-bold text-primary">
                          {group.cls.name}
                          <span className="ml-2 text-xs font-normal text-gray-500">
                            {group.editable.length + group.readonly.length}{' '}
                            student
                            {group.editable.length + group.readonly.length === 1
                              ? ''
                              : 's'}
                          </span>
                        </span>
                        <span className="flex items-center gap-1">
                          <button
                            onClick={() =>
                              downloadCsv([group], group.cls.name)
                            }
                            title={`Export ${group.cls.name} to CSV`}
                            className="p-1.5 rounded-lg text-gray-500 hover:text-primary hover:bg-white dark:hover:bg-gray-800"
                          >
                            <FileSpreadsheet size={15} />
                          </button>
                          <button
                            onClick={() => exportPdf([group], group.cls.name)}
                            disabled={busy !== null}
                            title={`Export ${group.cls.name} to PDF`}
                            className="p-1.5 rounded-lg text-gray-500 hover:text-primary hover:bg-white dark:hover:bg-gray-800 disabled:opacity-50"
                          >
                            <FileText size={15} />
                          </button>
                        </span>
                      </div>
                    )}

                    {group.editable.map((s) => {
                      flatIdx += 1;
                      return renderRow(s, flatIdx);
                    })}

                    {group.readonly.length > 0 && (
                      <div className="flex items-center gap-2 px-2 pt-1 text-[11px] font-medium text-gray-400">
                        <Lock size={11} />
                        Taught by other teachers — read-only
                        <div className="flex-1 border-t border-dashed border-gray-200 dark:border-gray-700" />
                      </div>
                    )}
                    {group.readonly.map((s) => {
                      flatIdx += 1;
                      return renderRow(s, flatIdx);
                    })}
                  </div>
                ));
              })()}
            </div>
          )}

          {rosterStudents.length > 0 && (
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 text-xs text-gray-500">
              <span>
                Enter 0–100, or <strong>A</strong>/<strong>ABS</strong> for
                absent. Enter / ↑ ↓ moves down a column.{' '}
                {usesExam
                  ? 'Grade comes from the exam mark.'
                  : 'Term mark = average of CA months (absent ignored).'}
              </span>
              <Button
                icon={<Save size={15} />}
                loading={saving}
                disabled={!editable || !dirty}
                onClick={save}
              >
                Save
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Bottom sticky save button */}
      {dirty && editable && hasSelection && surface === 'marks' && (
        <div className="sticky bottom-4 z-30 mt-4">
          <button
            onClick={save}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#1b3a6b] text-white font-semibold rounded-xl shadow-2xl hover:bg-[#2d5295] active:scale-[0.98] transition-all"
          >
            {saving ? 'Saving…' : 'Save Marks'}
          </button>
        </div>
      )}

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
      {staleBlocked && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
            <p className="font-semibold text-gray-900 dark:text-white">App out of date</p>
            <p className="text-sm text-gray-500 mt-2">
              This app is out of date and can't save. Reload to get the latest
              version - your entries stay on screen.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-[#1b3a6b] text-white text-sm font-semibold rounded-lg"
            >
              Reload
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
